import { Button } from '@parcel/ui';
import { type VirtualItem, useWindowVirtualizer } from '@tanstack/react-virtual';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

export const DATA_TABLE_ROW_HEIGHT = 56;
/** Two-line project cells (primary + subtitle) need more than the single-line row height. */
export const DATA_TABLE_MULTI_LINE_ROW_HEIGHT = 64;
export const DATA_TABLE_PREFETCH_THRESHOLD = 20;
export const DATA_TABLE_OVERSCAN = 8;
/**
 * Sticky offset for table headers.
 * Must be `top-0`: `DataTableViewport` uses `overflow-x-auto`, which becomes the sticky
 * scrollport. A `top-14` (app-header) offset was incorrectly applied here and shoved header
 * labels down into the first body row.
 */
export const DATA_TABLE_STICKY_HEADER_TOP_CLASS = 'top-0';

export function shouldPrefetchNextPage({
  lastVirtualIndex,
  loadedCount,
  threshold = DATA_TABLE_PREFETCH_THRESHOLD,
}: {
  lastVirtualIndex: number;
  loadedCount: number;
  threshold?: number;
}): boolean {
  if (loadedCount <= 0 || lastVirtualIndex < 0) return false;

  return lastVirtualIndex >= Math.max(0, loadedCount - Math.max(0, threshold));
}

function measureScrollMargin(element: HTMLElement | null): number {
  if (!element) return 0;
  return element.getBoundingClientRect().top + window.scrollY;
}

function scrollMarginObserveTarget(listElement: HTMLElement | null): HTMLElement | null {
  if (!listElement) return null;
  // Prefer the table root so filter-bar / summary sibling height changes remeasure
  // scrollMargin even when the tbody itself does not resize.
  return listElement.closest('[data-infinite-table-root]') ?? listElement;
}

export function useInfiniteTableVirtualizer({
  rowCount,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  estimateSize = DATA_TABLE_ROW_HEIGHT,
}: {
  rowCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  estimateSize?: number;
}): {
  listRef: React.RefObject<HTMLTableSectionElement>;
  virtualItems: VirtualItem[];
  paddingTop: number;
  paddingBottom: number;
  scrollToTop: () => void;
} {
  const listRef = React.useRef<HTMLTableSectionElement>(null);
  const requestedRowCountRef = React.useRef<number | null>(null);
  const [scrollMargin, setScrollMargin] = React.useState(0);

  // Appended pages move the table root without necessarily resizing it, and jsdom/older
  // browsers have no ResizeObserver at all, so the row count drives the remeasure.
  // biome-ignore lint/correctness/useExhaustiveDependencies: rowCount is a manual remeasure trigger, not a value the effect reads.
  React.useLayoutEffect(() => {
    const update = () => setScrollMargin(measureScrollMargin(listRef.current));
    update();
    const observeTarget = scrollMarginObserveTarget(listRef.current);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (observer && observeTarget) observer.observe(observeTarget);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [rowCount]);

  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => estimateSize,
    overscan: DATA_TABLE_OVERSCAN,
    scrollMargin,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const firstVirtualItem = virtualItems[0];
  const lastVirtualItem = virtualItems[virtualItems.length - 1];
  const paddingTop = firstVirtualItem ? Math.max(0, firstVirtualItem.start - scrollMargin) : 0;
  const paddingBottom = lastVirtualItem
    ? Math.max(0, virtualizer.getTotalSize() - lastVirtualItem.end)
    : 0;
  const lastVirtualIndex = lastVirtualItem?.index ?? -1;
  const shouldPrefetch = shouldPrefetchNextPage({ lastVirtualIndex, loadedCount: rowCount });

  React.useEffect(() => {
    if (!shouldPrefetch) {
      requestedRowCountRef.current = null;
      return;
    }
    if (!hasNextPage || isFetchingNextPage || requestedRowCountRef.current === rowCount) {
      return;
    }

    requestedRowCountRef.current = rowCount;
    void fetchNextPage().catch(() => undefined);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, rowCount, shouldPrefetch]);

  // Page top, not the first row: a new view or sort should show the map, filters and column headers.
  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: 0, left: window.scrollX });
  }, []);

  return { listRef, virtualItems, paddingTop, paddingBottom, scrollToTop };
}

export function formatLoadedStatus({
  loaded,
  total,
  hasMore,
  loading,
}: {
  loaded: number;
  total: number | null;
  hasMore: boolean;
  loading: boolean;
}): string {
  if (loading) return 'Loading more…';

  const formattedLoaded = loaded.toLocaleString('en-US');
  if (total === null) return `${formattedLoaded} loaded`;

  const formattedTotal = total.toLocaleString('en-US');
  return hasMore
    ? `${formattedLoaded} of ${formattedTotal} loaded`
    : `All ${formattedTotal} loaded`;
}

export function DataTableLoadStatus({
  loaded,
  total,
  hasMore,
  isFetchingNextPage,
  isFetchNextPageError,
  onRetry,
}: {
  loaded: number;
  total: number | null;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <div className="flex min-h-12 items-center justify-between gap-3 px-6 py-2 text-sm text-muted-foreground">
      <span aria-live="polite" className="inline-flex items-center gap-2">
        {isFetchingNextPage ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
        ) : null}
        {formatLoadedStatus({
          loaded,
          total,
          hasMore,
          loading: isFetchingNextPage,
        })}
      </span>
      {isFetchNextPageError && !isFetchingNextPage ? (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}
